// src/pages/Wallet/SendModal.js
/* global BigInt */
import React, { useState, useContext, useEffect } from 'react';
import { NetworkContext } from '../../../contexts/NetworkContext';
import { formatUnits, parseUnits } from 'ethers';
import { useTranslation } from 'react-i18next';
import './SendModal.css';

const SendModal = ({ 
  isOpen, 
  onClose, 
  onSend, 
  token, 
  tokenBalances = {}, 
  usdPrice,
  dynamicGasPrice,
  gasOption,
  customGasPrice
}) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [receiver, setReceiver] = useState('');
  const [maxAmount, setMaxAmount] = useState('0');
  const [error, setError] = useState(null);

  const { currentChain } = useContext(NetworkContext);
  const nativeTokenSymbol =
    currentChain && currentChain.nativeTokenName
      ? currentChain.nativeTokenName.includes('(')
        ? currentChain.nativeTokenName.split('(')[1].replace(')', '').trim()
        : currentChain.nativeTokenName
      : 'ETH';

  // Use the passed token prop if provided; otherwise, default to native token
  const selectedToken = token ? token : { type: "native", symbol: nativeTokenSymbol };
  const tokenSymbol = selectedToken.address ? selectedToken.symbol : nativeTokenSymbol;
  const isNativeToken = !selectedToken.address || selectedToken.address.toLowerCase() === 'native';

  // Get the balance for the selected token
  const tokenAddress = selectedToken.address ? selectedToken.address.toLowerCase() : 'native';
  const tokenBalance = tokenBalances[tokenAddress] || '0';
  
  // Format the balance with commas for thousands
  const formattedBalance = parseFloat(tokenBalance).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6
  });

  // Calculate USD equivalent for the entered amount if usdPrice is provided
  const parsedAmount = parseFloat(amount);
  const usdEquivalent =
    !isNaN(parsedAmount) && usdPrice > 0
      ? (parsedAmount * usdPrice).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 4 })
      : null;

  // Calculate max sendable amount (accounting for gas if it's a native token)
  useEffect(() => {
    if (!tokenBalance) {
      setMaxAmount('0');
      return;
    }

    if (isNativeToken) {
      try {
        // Convert balance to BigInt for precise calculations
        const balanceBN = parseUnits(tokenBalance);
        
        // Calculate gas fee based on settings
        const gasUnits = 21000; // Standard gas units for native transfer
        
        // Determine effective gas price based on settings and network data
        let effectiveGasPrice;
        if (dynamicGasPrice) {
          const baseGasPrice = parseFloat(dynamicGasPrice);
          let multiplier;
          
          switch (gasOption) {
            case 'slow': multiplier = 0.8; break;
            case 'fast': multiplier = 1.2; break;
            case 'custom': 
              effectiveGasPrice = parseUnits(customGasPrice || '3', 'gwei');
              break;
            default: multiplier = 1.0; // medium
              effectiveGasPrice = parseUnits((baseGasPrice * multiplier).toFixed(9), 'gwei');
          }
          
          if (!effectiveGasPrice) {
            effectiveGasPrice = parseUnits((baseGasPrice * multiplier).toFixed(9), 'gwei');
          }
        } else {
          // Fallback if dynamic gas price isn't available
          effectiveGasPrice = parseUnits(customGasPrice || '3', 'gwei');
        }
        
        // Calculate total gas cost
        const gasCostBN = effectiveGasPrice * BigInt(gasUnits);
        
        // Add a 10% buffer to gas cost to account for price fluctuations
        const gasCostWithBuffer = gasCostBN + (gasCostBN * BigInt(10) / BigInt(100));
        
        // If balance is less than gas cost, set max to 0
        if (balanceBN <= gasCostWithBuffer) {
          setMaxAmount('0');
          return;
        }
        
        // Subtract gas cost from balance
        const maxSendable = balanceBN - gasCostWithBuffer;
        
        // Convert back to string with appropriate decimal formatting
        const maxSendableFormatted = formatUnits(maxSendable);
        setMaxAmount(maxSendableFormatted);
      } catch (err) {
        console.error("Error calculating max sendable amount:", err);
        // Fallback: use 90% of balance as a rough estimate
        setMaxAmount((parseFloat(tokenBalance) * 0.9).toFixed(6));
      }
    } else {
      // For ERC-20 tokens, max is the full balance
      setMaxAmount(tokenBalance);
    }
  }, [tokenBalance, isNativeToken, dynamicGasPrice, gasOption, customGasPrice]);

  // Handler for MAX button click
  const handleMaxClick = () => {
    setAmount(maxAmount);
    setError(null);
  };

  // Validate amount when it changes
  useEffect(() => {
    if (!amount) {
      setError(null);
      return;
    }

    const parsedAmount = parseFloat(amount);
    const parsedBalance = parseFloat(tokenBalance);

    if (isNaN(parsedAmount)) {
      setError(t('wallet.errors.numberRequired'));
    } else if (parsedAmount <= 0) {
      setError(t('wallet.errors.positiveAmount'));
    } else if (parsedAmount > parsedBalance) {
      setError(t('errors.insufficientBalance'));
    } else if (isNativeToken && parsedAmount > parseFloat(maxAmount)) {
      setError(t('errors.insufficientGasFunds'));
    } else {
      setError(null);
    }
  }, [amount, tokenBalance, maxAmount, isNativeToken, t]);

  if (!isOpen) return null;

  const handleSendClick = () => {
    if (!error && amount && receiver) {
      onSend(selectedToken, amount, receiver);
    }
  };

  return (
    <div className="send-modal-overlay">
      <div className="send-modal-content">
        <h2>{t('wallet.sendToken', { token: tokenSymbol })}</h2>

        <label className="send-modal-label" htmlFor="amount">
          {t('wallet.amount')}
        </label>
        <div className="balance-display">
          {t('wallet.balance', { value: `${formattedBalance} ${tokenSymbol}` })}
          {isNativeToken && parseFloat(maxAmount) < parseFloat(tokenBalance)}
        </div>
        
        <div className="amount-input-container">
          <input
            id="amount"
            className={`send-modal-input ${error ? 'input-error' : ''}`}
            type="text"
            placeholder={t('wallet.placeholders.enterAmount')}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <button
            className="max-amount-btn"
            onClick={handleMaxClick}
            disabled={!tokenBalance || parseFloat(maxAmount) <= 0}
            title={t('wallet.maxButton')}
          >
            {t('actions.max')}
          </button>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        {usdEquivalent && (
          <div className="usd-equivalent">
            {t('wallet.usdEquivalent', { amount: usdEquivalent })}
          </div>
        )}

        <label className="send-modal-label" htmlFor="receiver">
          {t('wallet.receiver')}
        </label>
        <input
          id="receiver"
          className="send-modal-input"
          type="text"
          placeholder={t('wallet.placeholders.enterAddress')}
          value={receiver}
          onChange={(e) => setReceiver(e.target.value)}
        />

        {/* Button row with proper spacing */}
        <div className="button-row">
          <div className="button-cell">
            <button 
              className="button-common send-btn" 
              onClick={handleSendClick}
              disabled={!!error || !amount || !receiver}
            >
              {t('actions.send')}
            </button>
          </div>
          <div className="button-cell">
            <button 
              className="button-common cancel-btn" 
              onClick={onClose}
            >
              {t('actions.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendModal;